import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useBackend, clearRequestCache } from '@/composables/backend'
import { clearSelectCache } from '@/composables/selectCache'
import { useDashboardConfigStore } from './dashboardConfigStore'

const STORE_NAME = 'auth'
const baseUri = process.env.VUE_APP_API_URL

export const useAuthStore = defineStore(STORE_NAME, () => {

    // Initialize CSRF token from localStorage for persistence across Incognito/iPhone
    const csrfToken = ref(localStorage.getItem('csrf_token') || null)

    const lastModule = ref(null);

    function saveLastModule(moduleName) {
        lastModule.value = moduleName;
    }

    function getLastModule() {
        return lastModule.value;
    }

    const { item: currentUser, getItem } = useBackend('auth')
    const { item: currentTenant, getItem: getTenant } = useBackend('admin')
    const dashboardConfigStore = useDashboardConfigStore()

    const payRollIntegrationName = computed( () => {
        return currentTenant?.value?.integrations?.find(a => a.type === "PayrollSystem")?.name || "Payroll Provider"
    })

    const posIntegrationName = computed( () => {
        return currentTenant?.value?.integrations?.find(a => a.type === "PosSystem")?.name || "Point of Sale System"
    })

    const accountingIntegrationName = computed( () => {
        return currentTenant?.value?.integrations?.find(a => a.type === "AccountingSystem")?.name || "Accounting Software"
    })

    const permissions = computed(() => currentUser.value?.permissions || [])

    const hasPermission = (permission) => {
        return permissions.value.includes(permission)
    }
    
    const isLoggedIn = computed(() => {
        return (csrfToken.value !== null)
    })

    const isImpersonating = computed( () => {
        return currentUser.value?.impersonatedTenantId !== null 
    })

    async function login(username, password){
        const res = await fetch(`${baseUri}/auth/login`, {
            method: 'POST',
            body: JSON.stringify({ username, password }),
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include'
        })

        if (!res.ok) {
            if (res.status === 401) {
                throw new Error('Incorrect username or password.')
            } else {
                throw new Error(`Login failed with status: ${res.status}`)
            }
        }

        const data = await res.json()

        // Store CSRF token in both ref and localStorage for Incognito/iPhone compatibility
        csrfToken.value = data.csrfToken || null
        if (data.csrfToken) {
            localStorage.setItem('csrf_token', data.csrfToken)
        } else {
            localStorage.removeItem('csrf_token')
        }

        // Small delay to ensure cookies are set before making subsequent requests
        // Critical for Incognito mode where SameSite=None cookies may have delayed processing
        await new Promise(resolve => setTimeout(resolve, 100))

        await getItem('me')
        await getTenant('tenant')
        await dashboardConfigStore.loadAll(true)
        clearRequestCache()
        clearSelectCache()

        return data
    }

    async function logout(){
        try {
            await fetch(`${baseUri}/auth/logout`, {
              method: 'POST',
              credentials: 'include'
            })
        } catch (error) {
        console.error('Logout request failed, but clearing local state anyway.', error)
        }

        // Clear local store and localStorage
        currentUser.value = null
        currentTenant.value = null
        csrfToken.value = null
        localStorage.removeItem('csrf_token')
        // Note: isImpersonating is a computed property derived from currentUser,
        // so setting currentUser.value = null automatically makes it false
        dashboardConfigStore.reset()
        clearRequestCache()
        clearSelectCache()
    }

    async function impersonate(tenantId) {
        try {
            const res = await fetch(`${baseUri}/platform/tenants/${tenantId}/impersonate`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'x-csrf-token': csrfToken.value || localStorage.getItem('csrf_token')
                }
            })

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const data = await res.json();

            // Update CSRF token if provided, persist to localStorage
            if (data.csrfToken) {
                csrfToken.value = data.csrfToken
                localStorage.setItem('csrf_token', data.csrfToken)
            }

        await getItem('me');
        await getTenant('tenant')
        await dashboardConfigStore.loadAll(true)
        clearRequestCache()
        clearSelectCache()

            return data

        } catch (error) {
            console.error("Failed to impersonate:", error);
            throw error;
        }
    }

    async function revertImpersonation() {
        try {
            const res = await fetch(`${baseUri}/platform/tenants/revert-impersonation`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'x-csrf-token': csrfToken.value || localStorage.getItem('csrf_token')
                }
            })
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`)
            }

            // Note: isImpersonating is a computed property that will automatically
            // update when getItem('current') refreshes currentUser below

            await getItem('me')
            await getTenant('tenant')
            await dashboardConfigStore.loadAll(true)
            clearRequestCache()
            clearSelectCache()

        } catch (error) {
            console.error("Failed to revert impersonation:", error);
            throw error;
        }
    }

    return { 
        // state
        csrfToken,
        currentUser,
        currentTenant,

        // getters/computed
        payRollIntegrationName,
        posIntegrationName,
        accountingIntegrationName,
        isLoggedIn,
        isImpersonating,
        permissions,
        hasPermission,

        // actions
        login,
        logout,
        impersonate,
        revertImpersonation,

        // optional module tracking
        lastModule,
        saveLastModule,
        getLastModule
    }
}, { persist: true })
